import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { subscriptionService } from '../../services/subscriptionService';
import CreateSubscription from './CreateSubscription';
import EditSubscription from './EditSubscription';
import './SubscriptionManagement.css';

const SubscriptionManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SubscriptionManagement.js:useEffect-theme',message:'Theme changed detected',data:{isDark,dataTheme:document.documentElement.getAttribute('data-theme')},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion agent log
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getUserSubscriptions();
      if (response.success) {
        setSubscriptions(response.subscriptions || []);
      } else {
        setError(response.message || 'Failed to load subscriptions');
        setSubscriptions([]); // Ensure it's always an array
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      setError('Failed to load subscriptions');
      setSubscriptions([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (subscriptionId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const response = await subscriptionService.updateSubscriptionStatus(subscriptionId, newStatus);
      
      if (response.success) {
        setSuccess(`Subscription ${newStatus === 'active' ? 'activated' : 'paused'} successfully`);
        loadSubscriptions();
      } else {
        setError(response.message || 'Failed to update subscription status');
      }
    } catch (error) {
      console.error('Error toggling subscription status:', error);
      setError('Failed to update subscription status');
    }
  };

  const handleDeleteSubscription = async () => {
    if (!subscriptionToDelete) return;

    try {
      const response = await subscriptionService.deleteSubscription(subscriptionToDelete.subscriptionId || subscriptionToDelete._id);
      
      if (response.success) {
        setSuccess('Subscription deleted successfully');
        loadSubscriptions();
      } else {
        setError(response.message || 'Failed to delete subscription');
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
      setError('Failed to delete subscription');
    } finally {
      setDeleteConfirmOpen(false);
      setSubscriptionToDelete(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFrequencyText = (frequency) => {
    switch (frequency) {
      case 'weekly':
        return 'Every Week';
      case 'biweekly':
        return 'Every 2 Weeks';
      case 'monthly':
        return 'Every Month';
      case 'quarterly':
        return 'Every 3 Months';
      default:
        return frequency;
    }
  };

  const formatNextDelivery = (date) => {
    const nextDate = new Date(date);
    const today = new Date();
    const diffTime = nextDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays > 0) {
      return `In ${diffDays} days`;
    } else {
      return 'Overdue';
    }
  };

  const calculateTotalAmount = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + ((item.price || 0) * (item.quantity || 0)), 0);
  };

  if (loading) {
    return (
      <Box 
        className="subscription-management" 
        sx={{ 
          p: { xs: 2, md: 4 },
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(240, 249, 255, 0.5)'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: isDarkMode ? '#60A5FA' : '#1E40AF', mb: 2 }} />
          <Typography sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
            Loading subscriptions...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Ensure subscriptions is always an array
  const safeSubscriptions = subscriptions || [];

  return (
    <Box 
      className="subscription-management" 
      sx={{ 
        minHeight: '100vh',
        bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(240, 249, 255, 0.5)',
        pb: 4
      }}
    >
      {/* Hero Section */}
      <Box
        className="subscription-hero"
        sx={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.2) 0%, rgba(37, 99, 235, 0.15) 100%)'
            : 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)',
          borderRadius: { xs: 0, md: 3 },
          p: { xs: 3, md: 5 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDarkMode
              ? 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)'
              : 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '1200px', mx: 'auto' }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              mb: 2,
              background: isDarkMode
                ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            Medicine Subscriptions
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3,
              color: isDarkMode ? '#BFDBFE' : '#64748B',
              fontSize: { xs: '0.9375rem', md: '1.0625rem' },
              textAlign: { xs: 'center', md: 'left' },
              maxWidth: '600px'
            }}
          >
            Manage your recurring medicine orders with automatic delivery and payment. Never miss a dose with our subscription service.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                  : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                color: '#FFFFFF',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: isDarkMode
                  ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                  : '0 4px 12px rgba(30, 64, 175, 0.25)',
                '&:hover': {
                  background: isDarkMode
                    ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                    : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                  boxShadow: isDarkMode
                    ? '0 8px 20px rgba(59, 130, 246, 0.4)'
                    : '0 8px 20px rgba(30, 64, 175, 0.35)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Create New Subscription
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Content Container */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, md: 3 } }}>

        {/* Subscriptions Grid */}
        {safeSubscriptions.length === 0 ? (
        <Paper 
          elevation={0}
          className="subscription-empty-paper"
          sx={{ 
            p: { xs: 3, md: 5 }, 
            textAlign: 'center',
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            color: isDarkMode ? '#E0F2FE' : '#1E293B',
            border: isDarkMode 
              ? '1px solid rgba(59, 130, 246, 0.3)' 
              : '1px solid rgba(30, 64, 175, 0.1)',
            borderRadius: 3,
            maxWidth: '600px',
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: isDarkMode
                ? 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)'
                : 'linear-gradient(90deg, #1E40AF 0%, #2563EB 100%)'
            }
          }}
        >
          <ScheduleIcon sx={{ 
            fontSize: { xs: 56, md: 72 }, 
            color: isDarkMode ? '#60A5FA' : '#1E40AF', 
            mb: 2,
            opacity: 0.8
          }} />
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{
              color: isDarkMode ? '#E0F2FE' : '#1E293B',
              fontWeight: 700,
              mb: 1.5
            }}
          >
            No Subscriptions Yet
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3,
              color: isDarkMode ? '#BFDBFE' : '#64748B',
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            Create your first subscription to automate your medicine orders and never miss a dose.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              background: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              color: '#FFFFFF',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: isDarkMode
                ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                : '0 4px 12px rgba(30, 64, 175, 0.25)',
              '&:hover': {
                background: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                boxShadow: isDarkMode
                  ? '0 8px 20px rgba(59, 130, 246, 0.4)'
                  : '0 8px 20px rgba(30, 64, 175, 0.35)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Create Subscription
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {safeSubscriptions.map((subscription) => (
            <Grid item xs={12} sm={6} lg={4} key={subscription._id}>
              <Card 
                className="subscription-card" 
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
                  border: isDarkMode 
                    ? '1px solid rgba(59, 130, 246, 0.2)' 
                    : '1px solid rgba(30, 64, 175, 0.1)',
                  borderRadius: 3,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDarkMode
                      ? '0 12px 40px rgba(59, 130, 246, 0.2)'
                      : '0 12px 40px rgba(30, 64, 175, 0.15)',
                    borderColor: isDarkMode 
                      ? 'rgba(59, 130, 246, 0.4)' 
                      : 'rgba(30, 64, 175, 0.2)'
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: isDarkMode
                      ? 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)'
                      : 'linear-gradient(90deg, #1E40AF 0%, #2563EB 100%)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Status and Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={(subscription.status || 'unknown').toUpperCase()}
                      color={getStatusColor(subscription.status || 'unknown')}
                      size="small"
                    />
                    <Box>
                      <Tooltip title="Edit Subscription">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setEditDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={(subscription.status || 'unknown') === 'active' ? 'Pause' : 'Resume'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(subscription._id, subscription.status || 'unknown')}
                          disabled={(subscription.status || 'unknown') === 'cancelled'}
                        >
                          {(subscription.status || 'unknown') === 'active' ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Subscription">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSubscriptionToDelete(subscription);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Subscription Details */}
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      fontWeight: 700,
                      mb: 2
                    }}
                  >
                    {subscription.title || subscription.name || 'Untitled Subscription'}
                  </Typography>

                  {/* Items List */}
                  <Box 
                    sx={{ 
                      mb: 2,
                      flexGrow: 1,
                      bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(240, 249, 255, 0.5)',
                      borderRadius: 2,
                      p: 1.5
                    }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{
                        color: isDarkMode ? '#BFDBFE' : '#64748B',
                        fontWeight: 600,
                        mb: 1
                      }}
                    >
                      Items ({(subscription.items || []).length}):
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {(subscription.items || []).slice(0, 2).map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={item.name || item.productName || 'Product'}
                            secondary={`Qty: ${item.quantity || 1} × ৳${item.price || 0}`}
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              sx: { color: isDarkMode ? '#E0F2FE' : '#1E293B' }
                            }}
                            secondaryTypographyProps={{ 
                              variant: 'caption',
                              sx: { color: isDarkMode ? '#94A3B8' : '#64748B' }
                            }}
                          />
                        </ListItem>
                      ))}
                      {(subscription.items || []).length > 2 && (
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`+${(subscription.items || []).length - 2} more items`}
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              sx: { color: isDarkMode ? '#94A3B8' : '#64748B' }
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>

                  <Divider 
                    sx={{ 
                      my: 2,
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 64, 175, 0.1)'
                    }} 
                  />

                  {/* Subscription Info */}
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ScheduleIcon sx={{ 
                            fontSize: 18, 
                            mr: 1, 
                            color: isDarkMode ? '#60A5FA' : '#1E40AF' 
                          }} />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: isDarkMode ? '#94A3B8' : '#64748B',
                              fontWeight: 600
                            }}
                          >
                            Frequency
                          </Typography>
                        </Box>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            fontWeight: 500
                          }}
                        >
                          {getFrequencyText(subscription.frequency || 'monthly')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PaymentIcon sx={{ 
                            fontSize: 18, 
                            mr: 1, 
                            color: isDarkMode ? '#60A5FA' : '#1E40AF' 
                          }} />
                          <Typography 
                            variant="caption"
                            sx={{ 
                              color: isDarkMode ? '#94A3B8' : '#64748B',
                              fontWeight: 600
                            }}
                          >
                            Total Amount
                          </Typography>
                        </Box>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ 
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                            fontSize: '1.1rem'
                          }}
                        >
                          ৳{calculateTotalAmount(subscription.items).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Next Delivery */}
                  {(subscription.status || 'unknown') === 'active' && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: isDarkMode 
                          ? 'rgba(59, 130, 246, 0.1)' 
                          : 'rgba(30, 64, 175, 0.05)',
                        borderRadius: 2,
                        border: isDarkMode
                          ? '1px solid rgba(59, 130, 246, 0.2)'
                          : '1px solid rgba(30, 64, 175, 0.1)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ShippingIcon sx={{ 
                          fontSize: 18, 
                          mr: 1, 
                          color: isDarkMode ? '#60A5FA' : '#1E40AF' 
                        }} />
                        <Typography 
                          variant="caption"
                          sx={{ 
                            color: isDarkMode ? '#94A3B8' : '#64748B',
                            fontWeight: 600
                          }}
                        >
                          Next Delivery
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          mb: 0.5
                        }}
                      >
                        {formatNextDelivery(subscription.nextDeliveryDate || subscription.nextOrderDate)}
                      </Typography>
                      <Typography 
                        variant="caption"
                        sx={{ 
                          color: isDarkMode ? '#94A3B8' : '#64748B'
                        }}
                      >
                        {subscription.nextDeliveryDate ? new Date(subscription.nextDeliveryDate).toLocaleDateString() : 
                         subscription.nextOrderDate ? new Date(subscription.nextOrderDate).toLocaleDateString() : 'Not scheduled'}
                      </Typography>
                    </Box>
                  )}

                  {/* Order History */}
                  {subscription.orderHistory && subscription.orderHistory.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <HistoryIcon sx={{ 
                          fontSize: 18, 
                          mr: 1, 
                          color: isDarkMode ? '#60A5FA' : '#1E40AF' 
                        }} />
                        <Typography 
                          variant="caption"
                          sx={{ 
                            color: isDarkMode ? '#94A3B8' : '#64748B',
                            fontWeight: 600
                          }}
                        >
                          Last Order
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: isDarkMode ? '#E0F2FE' : '#1E293B'
                        }}
                      >
                        {new Date(subscription.orderHistory[subscription.orderHistory.length - 1].createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        )}
      </Box>

      {/* Create Subscription Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? '#E0F2FE' : '#1E293B',
          bgcolor: isDarkMode 
            ? 'rgba(30, 64, 175, 0.2)' 
            : 'rgba(30, 64, 175, 0.05)',
          borderBottom: isDarkMode 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(30, 64, 175, 0.1)'
        }}>
          Create New Subscription
        </DialogTitle>
        <DialogContent sx={{ bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF' }}>
          <CreateSubscription
            onSuccess={() => {
              setCreateDialogOpen(false);
              loadSubscriptions();
              setSuccess('Subscription created successfully!');
            }}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? '#E0F2FE' : '#1E293B',
          bgcolor: isDarkMode 
            ? 'rgba(30, 64, 175, 0.2)' 
            : 'rgba(30, 64, 175, 0.05)',
          borderBottom: isDarkMode 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(30, 64, 175, 0.1)'
        }}>
          Edit Subscription
        </DialogTitle>
        <DialogContent sx={{ bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF' }}>
          {selectedSubscription && (
            <EditSubscription
              subscription={selectedSubscription}
              onSuccess={() => {
                setEditDialogOpen(false);
                setSelectedSubscription(null);
                loadSubscriptions();
                setSuccess('Subscription updated successfully!');
              }}
              onCancel={() => {
                setEditDialogOpen(false);
                setSelectedSubscription(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none'
          }
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
          Delete Subscription
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: isDarkMode ? '#BFDBFE' : '#64748B' }}>
            Are you sure you want to delete this subscription? This action cannot be undone.
          </Typography>
          {subscriptionToDelete && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(240, 249, 255, 0.5)', 
              borderRadius: 2,
              border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(30, 64, 175, 0.1)'
            }}>
              <Typography 
                variant="subtitle2"
                sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B', fontWeight: 600 }}
              >
                {subscriptionToDelete.title || subscriptionToDelete.name || 'Untitled Subscription'}
              </Typography>
              <Typography 
                variant="body2"
                sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', mt: 0.5 }}
              >
                {(subscriptionToDelete.items || []).length} items • {getFrequencyText(subscriptionToDelete.frequency || 'monthly')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ color: isDarkMode ? '#BFDBFE' : '#64748B' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteSubscription} 
            color="error" 
            variant="contained"
            sx={{
              bgcolor: '#EF4444',
              '&:hover': { bgcolor: '#DC2626' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionManagement;
